/**
 * Natural-language quick-add parser. Turns "Call bank tomorrow 5pm !high #errands /quick"
 * into a task name plus structured fields, and reports every recognised token with its
 * position so the UI can render chips (and let the user dismiss one via `ignoredRanges`).
 *
 * All calendar decisions use `ctx.tz` and `ctx.now`; nothing assumes a particular zone.
 * Pure TypeScript: runs in the browser, in Deno Edge Functions and in tests.
 */
import { formatMonthDay, formatTime12, weekdayShort } from './format.ts';
import {
  addDays,
  addMonthsClamped,
  dayOfWeek,
  daysInMonth,
  diffDays,
  formatISODate,
  localDateTimeToInstant,
  parseISODate,
  startOfWeek,
  todayInTz,
} from './time.ts';
import type { ISODate, ISOTime, Priority } from './types.ts';

export type QuickAddTokenType = 'date' | 'time' | 'priority' | 'tag' | 'list' | 'recurrence';

export interface QuickAddToken {
  type: QuickAddTokenType;
  /** Inclusive start offset into the input (UTF-16 code units). */
  start: number;
  /** Exclusive end offset. */
  end: number;
  /** The exact input text covered, e.g. `at 5pm`. */
  text: string;
  /** Chip text, e.g. `Tomorrow`, `5pm`, `High`, `#errands`, `⚡ Quick Wins`, `Weekly`. */
  label: string;
}

export interface QuickAddList {
  id: string;
  name: string;
  emoji?: string | null;
  spaceName?: string | null;
  spaceId?: string | null;
}

export interface QuickAddTag {
  id: string;
  name: string;
  space_id: string;
}

export interface QuickAddContext {
  now: Date;
  tz: string;
  lists: QuickAddList[];
  tags: QuickAddTag[];
  /** Prefer lists/tags in this space. */
  currentSpaceId?: string | null;
  /** Ranges the user dismissed (chip ×): any token overlapping one is not recognised. */
  ignoredRanges?: Array<[number, number]>;
  /** Default 1 (Monday). Used by "next week". */
  weekStartsOn?: 0 | 1;
}

export type QuickAddRecurrenceKind = 'daily' | 'weekly' | 'monthly' | 'yearly' | 'custom';

export interface QuickAddRecurrence {
  rrule: string;
  label: string;
  kind: QuickAddRecurrenceKind;
}

export interface QuickAddResult {
  /** Input with recognised tokens removed, whitespace collapsed, trimmed. */
  name: string;
  /** Recognised tokens in input order. */
  tokens: QuickAddToken[];
  due_date: ISODate | null;
  /** `HH:mm` */
  due_time: ISOTime | null;
  priority: Priority | null;
  /** `id: null` = the tag does not exist yet and should be created inline. */
  tags: { name: string; id: string | null }[];
  list: QuickAddList | null;
  recurrence: QuickAddRecurrence | null;
}

// ---------------------------------------------------------------------------
// Grammar
// ---------------------------------------------------------------------------

/** Word characters (Unicode letters, digits, combining marks, underscore) for a character class. */
const W = '\\p{L}\\p{N}\\p{M}_';
/** End of a word token: no word character, hyphen, or `.word` (so `today.com` is not a date). */
const END_WORD = `(?![${W}\\-]|\\.[${W}])`;
/** End of a token ending in a number: as END_WORD and no `:` either. */
const END_NUM = `(?![${W}\\-:]|\\.[${W}])`;
/** End of a sigil token (`!high`, `*daily`) or a time with am/pm. */
const END_SIGIL = `(?![${W}]|\\.[${W}])`;
/** Optional possessive, so "Monday's review" yields the date and a clean name. */
const POSS = "(?:['’]s)?";
/** Optional connector absorbed into a date token: "on fri", "by tomorrow". */
const DATE_CONN = '(?:(?:on|by)\\s+)?';
/** Optional connector absorbed into a time token: "at 5pm", "by 5pm", "@ 5pm". */
const TIME_CONN = '(?:(?:at|by)\\s+|@\\s*)?';
const WEEKDAYS =
  'sunday|sun|monday|mon|tuesday|tues|tue|wednesday|wed|thursday|thurs|thur|thu|friday|fri|saturday|sat';
const MONTHS =
  'january|jan|february|feb|march|mar|april|apr|may|june|jun|july|jul|august|aug|september|sept|sep|october|oct|november|nov|december|dec';
const ORDINAL = '(?:st|nd|rd|th)?';
const YEAR = '(?:,?\\s+(20\\d{2}))?';

const WEEKDAY_INDEX: Record<string, number> = {
  sun: 0,
  mon: 1,
  tue: 2,
  wed: 3,
  thu: 4,
  fri: 5,
  sat: 6,
};
const MONTH_INDEX: Record<string, number> = {
  jan: 1,
  feb: 2,
  mar: 3,
  apr: 4,
  may: 5,
  jun: 6,
  jul: 7,
  aug: 8,
  sep: 9,
  oct: 10,
  nov: 11,
  dec: 12,
};
const RRULE_DAYS = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'] as const;

const PRIORITY_WORDS: Record<string, Priority> = { urgent: 1, high: 2, normal: 3, low: 4 };
const PRIORITY_LABELS: Record<Priority, string> = { 1: 'Urgent', 2: 'High', 3: 'Normal', 4: 'Low' };

type RecurrenceKey = 'daily' | 'weekdays' | 'weekly' | 'monthly' | 'yearly';
const RECURRENCE_ALIASES: Record<string, RecurrenceKey> = {
  daily: 'daily',
  weekdays: 'weekdays',
  weekday: 'weekdays',
  weekly: 'weekly',
  monthly: 'monthly',
  yearly: 'yearly',
  annually: 'yearly',
};

const re = (source: string): RegExp => new RegExp(source, 'giu');

const RE_RELATIVE_DAY = re(`${DATE_CONN}(tomorrow|tmrw|tmr|today|tod)${POSS}${END_WORD}`);
const RE_WEEKDAY = re(`${DATE_CONN}(?:(this|next)\\s+)?(${WEEKDAYS}|weekend)${POSS}${END_WORD}`);
const RE_NEXT_PERIOD = re(`${DATE_CONN}next\\s+(week|month)${POSS}${END_WORD}`);
const RE_IN_N = re(`in\\s+(\\d{1,3}|an?)\\s+(days?|weeks?|months?)${END_WORD}`);
const RE_DAY_MONTH = re(
  `${DATE_CONN}(\\d{1,2})${ORDINAL}\\s+(?:of\\s+)?(${MONTHS})${YEAR}${END_NUM}`,
);
const RE_MONTH_DAY = re(`${DATE_CONN}(${MONTHS})\\.?\\s+(\\d{1,2})${ORDINAL}${YEAR}${END_NUM}`);
const RE_TIME_12H = re(
  `${TIME_CONN}(\\d{1,2})(?:[:.](\\d{2}))?\\s?(a\\.m\\.|p\\.m\\.|am|pm)${END_SIGIL}`,
);
const RE_TIME_24H = re(`${TIME_CONN}([01]?\\d|2[0-3]):([0-5]\\d)(?![${W}:])`);
const RE_TIME_WORD = re(`${TIME_CONN}(noon|midnight)${END_WORD}`);
const RE_PRIORITY = re(`!(urgent|high|normal|low|[1-4])${END_SIGIL}`);
const RE_RECURRENCE = re(`\\*(daily|weekdays|weekday|weekly|monthly|yearly|annually)${END_SIGIL}`);
const RE_TAG = re(`#([${W}\\-]+)`);
const RE_LIST = re(`/([${W}\\-]+)(?:/([${W}\\-]+))?`);

/** A word/number token may not continue a word, a sigil, a decimal, an e-mail or a hyphenation. */
const BLOCKED_BEFORE_WORD = /[\p{L}\p{N}\p{M}_#/!*@.:\-$€£₹+&]/u;
/** A sigil token (`#`, `/`, `!`, `*`) must start the text or follow whitespace / an opening bracket. */
const ALLOWED_BEFORE_SIGIL = /[\s([{"'“‘]/u;

// ---------------------------------------------------------------------------
// Candidates
// ---------------------------------------------------------------------------

interface CandidateBase {
  start: number;
  end: number;
  text: string;
}

type CandidateData =
  | { type: 'date'; date: ISODate }
  | { type: 'time'; time: ISOTime }
  | { type: 'priority'; priority: Priority }
  | { type: 'tag'; tagText: string }
  | { type: 'list'; list: QuickAddList }
  | { type: 'recurrence'; recurrence: RecurrenceKey };

type Candidate = CandidateBase & CandidateData;

function scan(
  regex: RegExp,
  text: string,
  sigil: boolean,
  build: (m: RegExpExecArray) => CandidateData | null,
  out: Candidate[],
): void {
  regex.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = regex.exec(text)) !== null) {
    const prev = text[m.index - 1];
    const leftOk =
      prev === undefined ||
      (sigil ? ALLOWED_BEFORE_SIGIL.test(prev) : !BLOCKED_BEFORE_WORD.test(prev));
    if (leftOk) {
      const data = build(m);
      if (data) out.push({ start: m.index, end: m.index + m[0].length, text: m[0], ...data });
    }
    // Restart one character later so overlapping alternatives are also collected.
    regex.lastIndex = m.index + 1;
  }
}

function key3(word: string): string {
  return word.slice(0, 3).toLowerCase();
}

function weekdayDate(today: ISODate, target: number, mode: string | undefined): ISODate {
  const delta = (target - dayOfWeek(today) + 7) % 7;
  if (mode === 'this') return addDays(today, delta);
  const strictlyAfter = delta === 0 ? 7 : delta;
  return addDays(today, mode === 'next' ? strictlyAfter + 7 : strictlyAfter);
}

function absoluteDate(
  today: ISODate,
  month: number,
  day: number,
  year: number | null,
): ISODate | null {
  if (day < 1) return null;
  if (year !== null)
    return day <= daysInMonth(year, month) ? formatISODate(year, month, day) : null;
  // Longest the month can ever be (2024 is a leap year, so Feb allows 29).
  if (day > daysInMonth(2024, month)) return null;
  let y = parseISODate(today).year;
  // Already passed this year (or Feb 29 in a common year) → the next year it exists.
  while (day > daysInMonth(y, month) || formatISODate(y, month, day) < today) y++;
  return formatISODate(y, month, day);
}

function hhmm(hour: number, minute: number): ISOTime {
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

function collectCandidates(text: string, today: ISODate, ctx: QuickAddContext): Candidate[] {
  const out: Candidate[] = [];
  const weekStartsOn = ctx.weekStartsOn ?? 1;

  // Dates
  scan(
    RE_RELATIVE_DAY,
    text,
    false,
    (m) => {
      const word = m[1]!.toLowerCase();
      return { type: 'date', date: word.startsWith('tod') ? today : addDays(today, 1) };
    },
    out,
  );
  scan(
    RE_WEEKDAY,
    text,
    false,
    (m) => {
      const mode = m[1]?.toLowerCase();
      const word = m[2]!.toLowerCase();
      if (word === 'weekend') {
        // "weekend" / "this weekend" = the coming Saturday (today if Saturday); "next weekend" = +7.
        const saturday = weekdayDate(today, 6, 'this');
        return { type: 'date', date: mode === 'next' ? addDays(saturday, 7) : saturday };
      }
      return { type: 'date', date: weekdayDate(today, WEEKDAY_INDEX[key3(word)]!, mode) };
    },
    out,
  );
  scan(
    RE_NEXT_PERIOD,
    text,
    false,
    (m) => {
      if (m[1]!.toLowerCase() === 'week') {
        return { type: 'date', date: addDays(startOfWeek(today, weekStartsOn), 7) };
      }
      const { year, month } = parseISODate(addMonthsClamped(today, 1));
      return { type: 'date', date: formatISODate(year, month, 1) };
    },
    out,
  );
  scan(
    RE_IN_N,
    text,
    false,
    (m) => {
      const raw = m[1]!.toLowerCase();
      const n = raw === 'a' || raw === 'an' ? 1 : Number(raw);
      const unit = m[2]!.toLowerCase();
      const date = unit.startsWith('day')
        ? addDays(today, n)
        : unit.startsWith('week')
          ? addDays(today, 7 * n)
          : addMonthsClamped(today, n);
      return { type: 'date', date };
    },
    out,
  );
  scan(
    RE_DAY_MONTH,
    text,
    false,
    (m) => {
      const date = absoluteDate(
        today,
        MONTH_INDEX[key3(m[2]!)]!,
        Number(m[1]),
        m[3] ? Number(m[3]) : null,
      );
      return date ? { type: 'date', date } : null;
    },
    out,
  );
  scan(
    RE_MONTH_DAY,
    text,
    false,
    (m) => {
      const date = absoluteDate(
        today,
        MONTH_INDEX[key3(m[1]!)]!,
        Number(m[2]),
        m[3] ? Number(m[3]) : null,
      );
      return date ? { type: 'date', date } : null;
    },
    out,
  );

  // Times
  scan(
    RE_TIME_12H,
    text,
    false,
    (m) => {
      const hour = Number(m[1]);
      const minute = m[2] ? Number(m[2]) : 0;
      if (hour < 1 || hour > 12 || minute > 59) return null;
      const pm = m[3]!.toLowerCase().startsWith('p');
      return { type: 'time', time: hhmm((hour % 12) + (pm ? 12 : 0), minute) };
    },
    out,
  );
  scan(
    RE_TIME_24H,
    text,
    false,
    (m) => ({ type: 'time', time: hhmm(Number(m[1]), Number(m[2])) }),
    out,
  );
  scan(
    RE_TIME_WORD,
    text,
    false,
    (m) => ({
      type: 'time',
      time: m[1]!.toLowerCase() === 'noon' ? '12:00' : '00:00',
    }),
    out,
  );

  // Sigils
  scan(
    RE_PRIORITY,
    text,
    true,
    (m) => {
      const raw = m[1]!.toLowerCase();
      const priority = PRIORITY_WORDS[raw] ?? (Number(raw) as Priority);
      return { type: 'priority', priority };
    },
    out,
  );
  scan(
    RE_RECURRENCE,
    text,
    true,
    (m) => ({
      type: 'recurrence',
      recurrence: RECURRENCE_ALIASES[m[1]!.toLowerCase()]!,
    }),
    out,
  );
  scan(
    RE_TAG,
    text,
    true,
    (m) => {
      const tagText = m[1]!;
      // `#123` (no letter at all) is an issue number, not a tag.
      return /\p{L}/u.test(tagText) ? { type: 'tag', tagText } : null;
    },
    out,
  );
  scan(
    RE_LIST,
    text,
    true,
    (m) => {
      const list =
        m[2] !== undefined
          ? matchList(m[2], m[1]!, ctx.lists, ctx.currentSpaceId ?? null)
          : matchList(m[1]!, null, ctx.lists, ctx.currentSpaceId ?? null);
      return list ? { type: 'list', list } : null;
    },
    out,
  );

  return out;
}

// ---------------------------------------------------------------------------
// Fuzzy matching for lists and tags
// ---------------------------------------------------------------------------

/** Lowercase, Latin diacritics and emoji variation selectors removed, only letters/digits/marks kept. */
function compactKey(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[̀-ͯ︀-️]/g, '')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\p{M}]/gu, '');
}

/** `🎯 Priority Tasks` → `priority-tasks`. */
function hyphenKey(s: string): string {
  return s
    .replace(/^[^\p{L}\p{N}]+/u, '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-');
}

function matchScore(candidate: string, query: string): number {
  if (!query) return 0;
  if (candidate === query) return 3;
  if (candidate.startsWith(query)) return 2;
  return candidate.includes(query) ? 1 : 0;
}

/**
 * Best list for `/query` or `/space/query`: exact > startsWith > includes on the list name,
 * then the better space-name match, then the current space, then input order.
 */
function matchList(
  query: string,
  spaceQuery: string | null,
  lists: QuickAddList[],
  currentSpaceId: string | null,
): QuickAddList | null {
  const q = compactKey(query);
  const sq = spaceQuery === null ? null : compactKey(spaceQuery);
  let best: QuickAddList | null = null;
  let bestScore = 0;
  for (const list of lists) {
    const listScore = matchScore(compactKey(list.name), q);
    if (!listScore) continue;
    let spaceScore = 0;
    if (sq !== null) {
      spaceScore = matchScore(compactKey(list.spaceName ?? ''), sq);
      if (!spaceScore) continue;
    }
    const inCurrent = currentSpaceId !== null && list.spaceId === currentSpaceId ? 1 : 0;
    const score = listScore * 100 + spaceScore * 10 + inCurrent;
    if (score > bestScore) {
      best = list;
      bestScore = score;
    }
  }
  return best;
}

/**
 * Existing tag for `#typed`: same name after normalising (emoji stripped, spaces → `-`),
 * then same letters ignoring punctuation, then a unique prefix. Prefers `spaceId`.
 */
function matchTag(typed: string, tags: QuickAddTag[], spaceId: string | null): QuickAddTag | null {
  const pick = (pool: QuickAddTag[]): QuickAddTag | null =>
    pool.find((t) => t.space_id === spaceId) ?? pool[0] ?? null;

  const lower = typed.toLowerCase();
  const exact = tags.filter((t) => hyphenKey(t.name) === lower);
  if (exact.length) return pick(exact);

  const c = compactKey(typed);
  const compact = tags.filter((t) => compactKey(t.name) === c);
  if (compact.length) return pick(compact);

  // Unique prefix: first among the preferred space's tags, then among all tags. Tags with the
  // same name in different spaces count as one, so a template's per-space tags still match.
  const prefix = tags.filter((t) => compactKey(t.name).startsWith(c));
  for (const pool of [prefix.filter((t) => t.space_id === spaceId), prefix]) {
    if (new Set(pool.map((t) => hyphenKey(t.name))).size === 1) return pick(pool);
  }
  return null;
}

// ---------------------------------------------------------------------------
// Labels and assembly
// ---------------------------------------------------------------------------

function dateLabel(date: ISODate, today: ISODate): string {
  const days = diffDays(date, today);
  if (days === 0) return 'Today';
  if (days === 1) return 'Tomorrow';
  if (days > 1 && days < 7) return weekdayShort(date);
  const year = parseISODate(date).year;
  return year === parseISODate(today).year
    ? formatMonthDay(date)
    : `${formatMonthDay(date)}, ${year}`;
}

function listLabel(list: QuickAddList): string {
  return list.emoji && !list.name.startsWith(list.emoji) ? `${list.emoji} ${list.name}` : list.name;
}

const RECURRENCE_META: Record<RecurrenceKey, { label: string; kind: QuickAddRecurrenceKind }> = {
  daily: { label: 'Daily', kind: 'daily' },
  weekdays: { label: 'Weekdays', kind: 'weekly' },
  weekly: { label: 'Weekly', kind: 'weekly' },
  monthly: { label: 'Monthly', kind: 'monthly' },
  yearly: { label: 'Yearly', kind: 'yearly' },
};

function buildRecurrence(key: RecurrenceKey, due: ISODate): QuickAddRecurrence {
  let rrule: string;
  switch (key) {
    case 'daily':
      rrule = 'FREQ=DAILY;INTERVAL=1';
      break;
    case 'weekdays':
      rrule = 'FREQ=WEEKLY;INTERVAL=1;BYDAY=MO,TU,WE,TH,FR';
      break;
    case 'weekly':
      rrule = `FREQ=WEEKLY;INTERVAL=1;BYDAY=${RRULE_DAYS[dayOfWeek(due)]}`;
      break;
    case 'monthly':
      rrule = `FREQ=MONTHLY;INTERVAL=1;BYMONTHDAY=${parseISODate(due).day}`;
      break;
    case 'yearly':
      rrule = 'FREQ=YEARLY;INTERVAL=1';
      break;
  }
  return { rrule, ...RECURRENCE_META[key] };
}

/** Collapse whitespace; once tokens were cut out, also tidy the punctuation they leave behind. */
function tidyName(s: string, tokensRemoved: boolean): string {
  let out = s.replace(/\s+/g, ' ').trim();
  if (!tokensRemoved) return out;
  out = out
    .replace(/ ([,;:.!?])(?= |$)/g, '$1') // "Call bank , then" → "Call bank, then"
    .replace(/^(?:[-–—]+\s+|[,;:]+\s*)/, '') // "- call bank" / ", call bank"
    .replace(/(?:\s+[-–—]+|[,;:]+)$/, ''); // "Pay rent -" / "Pay rent,"
  return out.trim();
}

function overlaps(a: { start: number; end: number }, start: number, end: number): boolean {
  return a.start < end && start < a.end;
}

/**
 * Parse quick-add text. Rules (case-insensitive, whole words only):
 * - Dates: today/tod, tomorrow/tmr/tmrw, weekday names (strictly after today; `this` = upcoming
 *   incl. today; `next` = +7), next week, (this|next) weekend, next month, in N days/weeks/months,
 *   `12 oct`, `oct 12th`, `12 oct 2027`. A month name alone is never a date.
 * - Times: 5pm, 5 pm, 5:30pm, 5.30pm, 17:00, at/by/@ 5pm, noon, midnight. A bare number is not a time.
 *   A time without a date means today if still ahead of `now`, else tomorrow.
 * - Only the first date, time, priority, list and recurrence are used; later ones stay in the name.
 * - `!urgent|!high|!normal|!low|!1..!4`, `#tag` (many), `/list` or `/space/list`,
 *   `*daily|*weekdays|*weekly|*monthly|*yearly` (implies due today when no date is given).
 */
export function parseQuickAdd(text: string, ctx: QuickAddContext): QuickAddResult {
  const today = todayInTz(ctx.tz, ctx.now);
  const ignored = ctx.ignoredRanges ?? [];

  const candidates = collectCandidates(text, today, ctx)
    .filter((c) => !ignored.some(([s, e]) => overlaps(c, s, e)))
    .sort((a, b) => a.start - b.start || b.end - a.end);

  // Earliest start wins, then the longest; one of each kind except tags.
  const accepted: Candidate[] = [];
  const seen = new Set<QuickAddTokenType>();
  for (const c of candidates) {
    if (accepted.some((a) => overlaps(a, c.start, c.end))) continue;
    if (c.type !== 'tag' && seen.has(c.type)) continue;
    accepted.push(c);
    seen.add(c.type);
  }

  let dueDate: ISODate | null = null;
  let dueTime: ISOTime | null = null;
  let priority: Priority | null = null;
  let list: QuickAddList | null = null;
  let recurrenceKey: RecurrenceKey | null = null;
  for (const c of accepted) {
    if (c.type === 'date') dueDate = c.date;
    else if (c.type === 'time') dueTime = c.time;
    else if (c.type === 'priority') priority = c.priority;
    else if (c.type === 'list') list = c.list;
    else if (c.type === 'recurrence') recurrenceKey = c.recurrence;
  }

  if (dueTime !== null && dueDate === null) {
    const todayAt = localDateTimeToInstant(today, dueTime, ctx.tz);
    dueDate = todayAt.getTime() > ctx.now.getTime() ? today : addDays(today, 1);
  }
  if (recurrenceKey !== null && dueDate === null) dueDate = today;

  // Tags belong to a space: prefer the chosen list's space, else the current one.
  const tagSpaceId = list?.spaceId ?? ctx.currentSpaceId ?? null;
  const tags: QuickAddResult['tags'] = [];
  const tokens: QuickAddToken[] = accepted.map((c) => {
    const base = { type: c.type, start: c.start, end: c.end, text: c.text };
    switch (c.type) {
      case 'date':
        return { ...base, label: dateLabel(c.date, today) };
      case 'time':
        return { ...base, label: formatTime12(c.time) };
      case 'priority':
        return { ...base, label: PRIORITY_LABELS[c.priority] };
      case 'list':
        return { ...base, label: listLabel(c.list) };
      case 'recurrence':
        return { ...base, label: RECURRENCE_META[c.recurrence].label };
      case 'tag': {
        const match = matchTag(c.tagText, ctx.tags, tagSpaceId);
        const tag = match ? { name: match.name, id: match.id } : { name: c.tagText, id: null };
        const dup = tags.some((t) =>
          tag.id !== null
            ? t.id === tag.id
            : t.id === null && t.name.toLowerCase() === tag.name.toLowerCase(),
        );
        if (!dup) tags.push(tag);
        return { ...base, label: `#${tag.name}` };
      }
    }
  });

  let rest = '';
  let pos = 0;
  for (const c of accepted) {
    rest += `${text.slice(pos, c.start)} `;
    pos = c.end;
  }
  rest += text.slice(pos);

  return {
    name: tidyName(rest, accepted.length > 0),
    tokens,
    due_date: dueDate,
    due_time: dueTime,
    priority,
    tags,
    list,
    recurrence:
      recurrenceKey !== null && dueDate !== null ? buildRecurrence(recurrenceKey, dueDate) : null,
  };
}
